import { useCallback, useEffect, useRef, useState } from 'react';
import {
  CITATION_MARKER, slotAnswer, channelsFor, canOpenSourceDocuments, showsInternalSweep,
  systemFailureNotice,
  type AccountLevel,
} from '@lenderhound/shared';
import {
  recordAeContactClick,
  type AeContact, type Answer, type Channel, type Cited, type Coverage, type LenderGroup,
  type Reference, type SectionTitle,
} from '../api.ts';
import CitationViewer from './CitationViewer.tsx';
import ProgramMatrixViewer, { type QuotedProgram } from './ProgramMatrixViewer.tsx';
import SearchProgress, { type Stage } from './SearchProgress.tsx';

/**
 * Results.
 *
 * Three things here are load-bearing rather than decorative.
 *
 * Near-misses are an ALTERNATIVE to matches, never a companion to them. "Verus caps you
 * at 70%, you asked 75%, drop the loan to $1,487,500" is the most useful thing on the
 * page when nobody can do the file as it stands, and it is noise the moment somebody
 * can - a broker offered money now and money after rework does not experience that as a
 * choice. `applyResultSlots` makes the two lists exclusive; this file must not put them
 * back together by rendering one of them a second time under another heading.
 *
 * A lender whose channels disagree never collapses into one verdict. The broker's form
 * has no investor field and should not - they cannot be expected to know where the file
 * is going - so the strictest channel renders first and the disagreement is stated.
 * Optimism about delivery is how a broker eats a late decline. What a broker is never
 * shown is the DELIVERY: a channel is presented as one of the lender's own programs
 * ("Vanquish program"), because "delivered to TransAm" describes our supply chain to
 * somebody who is trying to place a loan, and names an investor they cannot call.
 *
 * And there are two audiences. A superuser sees everything below; a broker sees the
 * answer without the apparatus that produced it - no sweep, no coverage line, no grid
 * dump of every channel of every lender, no prose summary that walks the whole roster,
 * and no note about how old our copy of a matrix is. What is withheld is our accounting,
 * never the underwriting: every blocker, condition, carve-out, near-miss fix and open
 * question a finding carries is on a broker's page too, because those are what they act
 * on. The boundary is `showsInternalSweep`, and like `applyResultSlots` it is applied
 * HERE, in the view. The answer object arrives whole and stays whole - the email digest
 * still carries the summary, and the eval suite still scores every finding.
 */

const money = (n: number): string => `$${Math.round(n).toLocaleString()}`;

/**
 * Form fields named the way a broker would say them, for the widened-search banner.
 *
 * Duplicated from WIDENABLE_FIELD_LABELS in the shared package rather than imported,
 * because `coverage.widenedOn` arrives off the wire as plain strings and this map has to
 * survive a value it does not recognise - which the `?? f` fallback at the call site
 * does. An unknown key renders as the key, never as nothing.
 */
const WIDENED_LABELS: Record<string, string> = {
  loanPurpose: 'transaction type',
  occupancy: 'occupancy',
  propertyType: 'property type',
  state: 'state',
  ficoScore: 'FICO',
  docType: 'documentation type',
  citizenship: 'borrower status',
};

/**
 * jsdom has no layout, so it does not implement scrollIntoView at all - the property is
 * undefined and calling it throws a TypeError that surfaces as a render failure, not as
 * a missing scroll. Optional-chaining the REF does not help; the ref is present and the
 * method is not.
 */
function bringIntoView(el: HTMLElement | null): void {
  if (typeof el?.scrollIntoView === 'function') {
    el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }
}

/**
 * A conditional yes is a YES, and it is now coloured like one.
 *
 * It used to be amber, which put it in the same visual class as the widened-search
 * warning and the stale-document badge - i.e. the class of "something is wrong here" -
 * when what it actually says is that a lender will do the loan and has asked for
 * reserves. Every yes on this page is conditional on something; a clean yes is a yes
 * whose conditions the guideline did not bother to enumerate. Colouring the enumerated
 * one as a hazard punishes the lender that was specific.
 *
 * The two stay TOLD APART, in the word rather than the colour: "Yes · conditions", with
 * the conditions themselves listed on the card immediately below. That is the reason
 * green is safe here and would not be in the email, where a card has a headline and no
 * condition list, and colour is the only thing carrying the distinction at all.
 *
 * `eligible_with_conditions` - the GRID verdict, above - keeps its amber deliberately.
 * It renders only in the pre-answer grid dump, which is `sweep`-gated and therefore ours
 * alone: there the amber is triage ("this cell came with strings, read it before the
 * model does") and nobody it reaches is deciding where to send a file.
 */
function verdictPill(v: string) {
  const map: Record<string, [string, string]> = {
    eligible: ['pill-yes', 'Eligible'],
    eligible_with_conditions: ['pill-cond', 'Eligible · conditions'],
    ineligible: ['pill-no', 'Declined'],
    no_data: ['pill-unk', 'No document'],
    yes: ['pill-yes', 'Yes'],
    yes_with_conditions: ['pill-yes', 'Yes · conditions'],
    no: ['pill-no', 'No'],
    unknown: ['pill-unk', 'Unknown'],
  };
  const [cls, label] = map[v] ?? ['pill-unk', v];
  return <span className={`pill ${cls}`}>{label}</span>;
}

/** Render prose, turning [[c:N]] markers into clickable chips. */
function Prose({ text, onCite }: { text: string; onCite: (id: number) => void }) {
  const parts: React.ReactNode[] = [];
  let last = 0;
  // The marker regex is global and shared, so its lastIndex must be reset or a second
  // render silently starts mid-string and drops the first citations on the line.
  CITATION_MARKER.lastIndex = 0;
  for (let m = CITATION_MARKER.exec(text); m; m = CITATION_MARKER.exec(text)) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const id = Number(m[1]);
    parts.push(
      <button key={`${m.index}-${id}`} className="chip" type="button"
              onClick={() => onCite(id)} title="Show the source">
        ▸ {id}
      </button>,
    );
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return <>{parts}</>;
}

function CitedLine({ item, onCite }: { item: Cited; onCite: (id: number) => void }) {
  // Ids not already named inline still need to be reachable, or a citation the model
  // declared but did not mark becomes invisible - unverifiable in exactly the way the
  // whole design exists to prevent.
  CITATION_MARKER.lastIndex = 0;
  const inline = new Set([...item.text.matchAll(CITATION_MARKER)].map((m) => Number(m[1])));
  const extra = item.citationIds.filter((id) => !inline.has(id));
  return (
    <li style={{ marginBottom: 8, lineHeight: 1.55 }}>
      <Prose text={item.text} onCite={onCite} />
      {extra.map((id) => (
        <button key={id} className="chip" type="button" onClick={() => onCite(id)}>▸ {id}</button>
      ))}
    </li>
  );
}

/**
 * Joining a finding back to the grid rows it was written from.
 *
 * This is fuzzier than it should be, and the reason is worth stating because it decides
 * how much weight the join can carry. A finding's only handle on its channel is
 * `deliveryChannel`, a free STRING the model wrote after reading the descriptor
 * synthesize.ts renders for it - "base / retained" or "delivered to TransAm". It is not
 * a slug, it is not validated by the answer schema, and observed values already include
 * the bare "TransAm". So there is no key to join on; there is only a sentence to
 * recognise.
 *
 * Recognition is therefore conservative and returns a LIST. A lender with three programs
 * on the same investor produces three grid rows that a single channel sentence matches
 * equally well, and picking one of them arbitrarily would be inventing a fact. The
 * callers below take the maximum over whatever comes back and treat an empty list as
 * "unknown", which is exactly what it is.
 */
/* `norm`, the base-word list and `channelsFor` moved into shared/slots.ts with the
   slot rule they feed - see the note on `slotAnswer`. Imported rather than re-declared,
   because a second copy of this join is what put four different lenders in Henry's
   email. */

/**
 * The lender's program, named as the lender's own - never as our delivery.
 *
 * A card used to read "JLD Capital, LLC   delivered to TransAm". Two things are wrong
 * with that for a broker. It describes where WE send the paper, which is a fact about
 * our supply chain and not about their loan; and TransAm is not somebody they can ring,
 * so the one name on the card that looks actionable is the one that is not. The lender
 * sells these as programs, so they are printed as programs: "TransAm program",
 * "Vanquish program", or - for a retained channel, which has no second name to give -
 * nothing at all, leaving the lender's own name as the whole label.
 *
 * The grid row is preferred over the model's sentence because it carries the investor's
 * proper name rather than whatever the model typed. When nothing matched (a roster that
 * has not arrived, an investor we no longer hold a row for) the sentence is cleaned up
 * instead: the delivery preposition is what makes it read as logistics, so stripping it
 * turns "delivered to TransAm" back into "TransAm program" without inventing anything.
 */
const DELIVERY_PREFIX = /^(delivered\s+to|delivery\s+to|sold\s+to|to|via|through)\s+/i;
const BASE_ONLY = /^(base|retained|base\s*[/·-]\s*retained|portfolio|standard(\s+program)?|none|n\/a)$/i;

function programLabel(deliveryChannel: string, matched: readonly Channel[]): string | null {
  const investor = matched.find((c) => !c.isBaseChannel)?.investorName;
  if (investor) return `${investor} program`;
  if (matched.some((c) => c.isBaseChannel)) return null;
  const raw = deliveryChannel.trim().replace(DELIVERY_PREFIX, '').trim();
  if (!raw || BASE_ONLY.test(raw)) return null;
  return /programs?$/i.test(raw) ? raw : `${raw} program`;
}

/**
 * "View AE Contact" - the name, email and phone of the person at this lender.
 *
 * Every answer this product gives ends at an AE. "Confirm with your AE", "ask your AE
 * which investor this file goes to", and the whole unaddressed-subject path, which names
 * a lender and sends the broker to a person we could not name until migration 053. This
 * button is the end of that sentence.
 *
 * Behind a click rather than printed on the card for two reasons: four of seventeen
 * lenders have a contact, so printing it inline would make thirteen cards look broken;
 * and the click is the metric (054) - which lenders brokers actually reach out to, which
 * is the first evidence we will have that the four slots are showing the right four.
 *
 * A lender with no contact renders NOTHING. Not a disabled button, not "contact
 * unavailable": a dead control is a promise the page cannot keep, and the gap here is
 * ours rather than the lender's. The reveal is local state and never waits on the
 * network - the tracking POST is fired alongside it and its outcome is ignored, so a
 * missing route cannot cost a broker the phone number.
 */
function AeContactCard(
  { lenderId, contact, inquiryId }:
  { lenderId: string; contact: AeContact | null; inquiryId: string | null },
) {
  const [shown, setShown] = useState(false);
  // 053's CHECK makes a non-empty contact carry at least a name and an email, so this
  // pair is the whole test for "we were given somebody". Phone is genuinely optional.
  if (!contact?.name || !contact.email) return null;

  if (!shown) {
    /*
      A primary call-to-action, not a citation chip. It used to wear the chip's 11px
      outline, which put the one control on the card that ends in a phone call in the same
      visual class as the "▸ 7" beside every sentence - and the click is the metric, so a
      button nobody notices is a number that measures the styling rather than the lenders.
      The icon is decoration and hidden from the accessibility tree, so the button's name
      stays exactly "View AE Contact".
    */
    return (
      <button type="button" className="btn btn-ae"
              onClick={() => { setShown(true); void recordAeContactClick(lenderId, inquiryId); }}>
        <svg className="btn-ae-icon" viewBox="0 0 24 24" width="18" height="18"
             aria-hidden="true" focusable="false">
          <path fill="currentColor" d="M6.6 10.8a15.1 15.1 0 0 0 6.6 6.6l2.2-2.2a1 1 0 0 1 1-.25 11.4 11.4 0 0 0 3.6.57 1 1 0 0 1 1 1V20a1 1 0 0 1-1 1A17 17 0 0 1 3 4a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1c0 1.25.2 2.45.57 3.57a1 1 0 0 1-.25 1z" />
        </svg>
        View AE Contact
      </button>
    );
  }
  return (
    <div style={{ marginTop: 12, fontSize: 14, lineHeight: 1.6 }}>
      <div className="lbl">Your AE at this lender</div>
      <div style={{ fontWeight: 600 }}>{contact.name}</div>
      <div><a href={`mailto:${contact.email}`}>{contact.email}</a></div>
      {/* Phone as the client wrote it, NNN-NNN-NNNN - a `tel:` href takes the dashed
          form as-is, and 053 declined to invent a +1 nobody typed. */}
      {contact.phone ? <div><a href={`tel:${contact.phone}`}>{contact.phone}</a></div> : null}
    </div>
  );
}

/**
 * "View Matrix" - the lender's own matrix pages for the program this card is quoting.
 *
 * Henry, 2026-09-14: "Id like broker to view the full matrix associated with the program
 * that is being quoted." The citation chips already open a page, but only the page one
 * sentence was read off, behind a chip that looks like a footnote; this is the whole
 * matrix, one obvious click from the card.
 *
 * Outlined rather than filled, beside the AE button rather than instead of it. The call is
 * still the next action; the matrix is what the broker reads before making it.
 *
 * Same rule as AeContactCard: a card with no program to show renders no button at all.
 */
function ViewMatrixButton(
  { lenderName, programs }: { lenderName: string; programs: QuotedProgram[] },
) {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  if (programs.length === 0) return null;
  return (
    <>
      <button type="button" className="btn btn-matrix" onClick={() => setOpen(true)}>
        {/* Decoration, hidden from the accessibility tree: the name is "View Matrix". */}
        <svg className="btn-ae-icon" viewBox="0 0 24 24" width="18" height="18"
             aria-hidden="true" focusable="false">
          <path fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"
                d="M4 4h16v16H4zM4 9.5h16M4 14.5h16M9.5 4v16" />
        </svg>
        View Matrix
      </button>
      {open ? (
        <ProgramMatrixViewer lenderName={lenderName} programs={programs} onClose={close} />
      ) : null}
    </>
  );
}

/**
 * Which of a lender's programs a card is quoting, for "View Matrix".
 *
 * The same join `programLabel` leans on, and just as loose: a finding names its channel in
 * a sentence, so `matched` is whatever grid rows that sentence could be recognised as.
 * Among those, the rows that actually fit are the program being quoted - "base / retained"
 * matches every retained program a lender has, and the one the finding is about is the
 * one that cleared. When none of them cleared, the finding is a yes the guidelines made
 * out of a grid decline, and every matched row is as good a guess as the next.
 *
 * With nothing matched at all (a sentence we cannot read, a roster that never arrived for
 * this lender) the fallback is the lender's fitting programs, then all of them - a broker
 * who asked for the matrix is better served by choosing between a lender's grids than by
 * a button that is not there.
 *
 * Distinct by programId, because one program sold to three investors is three channels
 * and one matrix. Fitting programs first.
 */
const VERDICT_ORDER: Record<Channel['verdict'], number> = {
  eligible: 0, eligible_with_conditions: 1, ineligible: 2, no_data: 3,
};
const fits = (c: Channel): boolean =>
  c.verdict === 'eligible' || c.verdict === 'eligible_with_conditions';

function programsOf(channels: readonly Channel[]): QuotedProgram[] {
  const byId = new Map<string, QuotedProgram>();
  const ordered = [...channels].sort((a, b) => VERDICT_ORDER[a.verdict] - VERDICT_ORDER[b.verdict]);
  for (const c of ordered) {
    if (c.programId && !byId.has(c.programId)) {
      byId.set(c.programId, { programId: c.programId, programName: c.programName });
    }
  }
  return [...byId.values()];
}

/**
 * The programs the card's own words NAME, first. The channel join cannot tell a lender's
 * retained programs apart - every CapitalEdge program is "base / retained" - so on its own
 * it would open a Bronco finding on the plain DSCR tab. The model names what it is quoting,
 * though not always in full: "CapitalEdge Bronco DSCR 1-4 Unit" on the channel, "Bronco
 * DSCR: 70% max LTV" in the headline. So a program scores by how many of its name's words
 * the text carries, then by what share of the name that is - "Bronco DSCR 1-4 Unit" beats
 * "DSCR" on that headline (two words to one), and "DSCR" beats it on "CapitalEdge DSCR".
 *
 * A named program is offered even when its grid declined and a sibling's fit: the card is
 * about what it names. Unnamed programs follow, fitting ones only when any fit.
 */
const normName = (s: string): string => s.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
/** Words that sit in many program names and say nothing about which one. Digits too: "1-4". */
const NAME_NOISE = new Set(['program', 'programs', 'unit', 'units', 'the', 'and', 'of', 'for']);
const nameWords = (name: string): string[] =>
  normName(name).split(' ').filter((w) => w.length > 1 && !/^\d+$/.test(w) && !NAME_NOISE.has(w));

function rankByName(
  candidates: readonly QuotedProgram[], fallback: readonly QuotedProgram[], text: string,
): QuotedProgram[] {
  const said = new Set(normName(text).split(' '));
  const score = (p: QuotedProgram): [number, number] => {
    const words = nameWords(p.programName);
    const hits = words.filter((w) => said.has(w)).length;
    return [hits, words.length ? hits / words.length : 0];
  };
  const named = candidates
    .map((p) => ({ p, s: score(p) }))
    .filter((x) => x.s[0] > 0)
    // Stable, so equal scores keep the fitting-first order programsOf gave them.
    .sort((a, b) => (b.s[0] - a.s[0]) || (b.s[1] - a.s[1]))
    .map((x) => x.p);
  const seen = new Set(named.map((p) => p.programId));
  return [...named, ...fallback.filter((p) => !seen.has(p.programId))];
}

function quotedPrograms(
  own: readonly Channel[], matched: readonly Channel[], findingText = '',
): QuotedProgram[] {
  const pool = matched.length > 0 ? matched : own;
  const fitting = pool.filter(fits);
  return rankByName(programsOf(pool), programsOf(fitting.length > 0 ? fitting : pool),
                    findingText);
}

/**
 * A near-miss names no channel, so it gets every program we priced the file on, led by any
 * its own sentences name. `no_data` is left out because it means no cell at these
 * coordinates - a grid that says nothing about this loan is not the one the near-miss was
 * measured against.
 */
const nearMissPrograms = (own: readonly Channel[], text = ''): QuotedProgram[] => {
  const priced = programsOf(own.filter((c) => c.verdict !== 'no_data'));
  return rankByName(priced, priced, text);
};

interface Props {
  /**
   * Which account is reading. Decided by the server and carried on the session user, not
   * inferred here - and it changes what is DRAWN, not what arrived: the `verdicts` frame
   * still carries the whole lender roster to a broker's browser because the findings are
   * keyed by lenderId and that roster is the only thing that maps a UUID to a name.
   * Withholding it upstream would take the lender's name off every lender card.
   */
  level: AccountLevel;
  lenders: LenderGroup[] | null;
  coverage: Coverage | null;
  answer: Answer | null;
  /** Set only when no lender came back able to do the file. Decided server-side. */
  expertReview: string | null;
  /**
   * What the answer was built out of, decided server-side and rendered as a banner rather
   * than left to the prose.
   *
   * An answer that read no guideline at all is a narrower claim than one that read
   * eleven, and the two must not look alike. The summary is asked to say so and an open
   * question always does, but neither is visible at a glance, and a broker scanning a
   * results page reads the verdict pills.
   */
  basis: 'matrix_only' | 'matrix_and_guidelines' | null;
  /** Findings the citation validator removed. Zero almost always. */
  droppedFindings: number;
  /**
   * Lenders the search could not finish checking because our software failed.
   *
   * NOT the same kind of thing as anything else on this page, and that is the whole
   * reason it is its own prop rather than a line appended to the answer. Every other
   * caveat here is about the loan: a lender declined, a grid had no cell, a citation did
   * not hold up. This one is about us, it says nothing whatever about the file, and the
   * only response to it is to run the search again. Rendered for every account level -
   * a broker is the person this misleads.
   */
  systemFailures: { lenderSlug: string; lenderName: string }[];
  references: Map<number, Reference>;
  /**
   * Whether the reference set is final. It is not the same question as "is this map
   * empty": references stream in one event AFTER the answer, so between those two frames
   * the answer is fully rendered with clickable chips and nothing behind them. Without
   * this flag the two states - not here yet, and never coming - are indistinguishable,
   * and a chip clicked in that window reported a permanent failure about a lookup that
   * was still in flight.
   */
  referencesReady: boolean;
  stages: Stage[];
  /** See SearchProgress: these three exist only to make the ninety-second wait legible. */
  startedAt: number | null;
  sections: SectionTitle[] | null;
  failed: boolean;
  /** A chip clicked elsewhere (the chat) opens the same panel rather than a second one. */
  openCitationId?: number | null;
  onOpenHandled?: () => void;
  /**
   * The search an AE-contact click belongs to. Null until the `inquiry` frame lands, and
   * nullable for good after it: `ae_contact_click.inquiry_id` is nullable precisely
   * because the button will also sit somewhere reached without a search. A click with no
   * inquiry is still a click and is still recorded.
   */
  inquiryId?: string | null;
}

export default function Results({
  level, lenders, coverage, answer, expertReview, droppedFindings, basis, references,
  referencesReady, stages, startedAt, sections, failed, openCitationId, onOpenHandled,
  systemFailures,
  inquiryId = null,
}: Props) {
  /**
   * The system-failure notice, written once in @lenderhound/shared and rendered twice.
   *
   * Henry's digest calls the same function with the same lenders, because a page and a
   * mail that describe the same failure in two different sentences is how the four-slot
   * rule drifted before `slotAnswer` moved into that package.
   */
  const systemFailure = systemFailureNotice(systemFailures);
  const sweep = showsInternalSweep(level);
  const sourceViewable = canOpenSourceDocuments(level);
  /*
    Everything a finding knows about its lender, keyed by the UUID it carries.

    A finding is `{ lenderId, deliveryChannel, verdict, ... }` and nothing else. The name,
    the house rank, the grid rows and the AE all live on the verdicts frame, which arrives
    about eighty seconds earlier - so this roster is the only bridge between the two, and
    every lookup below goes through it. Falling back to a truncated id rather than to
    nothing keeps two different lenders distinguishable if the roster is ever missing.
  */
  const nameById = new Map<string, string>();
  const rankById = new Map<string, number>();
  const channelsById = new Map<string, Channel[]>();
  /** The roster flat, which is what `slotAnswer` decorates from. */
  const allChannels: Channel[] = [];
  const aeById = new Map<string, AeContact>();
  for (const g of lenders ?? []) {
    for (const c of g.channels) {
      nameById.set(c.lenderId, g.lenderName);
      rankById.set(c.lenderId, c.stackRank);
      allChannels.push(c);
      const held = channelsById.get(c.lenderId);
      if (held) held.push(c);
      else channelsById.set(c.lenderId, [c]);
      if (g.aeContact?.name && g.aeContact.email) aeById.set(c.lenderId, g.aeContact);
    }
  }
  const nameOf = (id: string) => nameById.get(id) ?? `Lender ${id.slice(0, 8)}`;
  // 100 is the unranked default the column itself uses (migration 019), so a lender the
  // roster does not mention sorts exactly where an unranked lender would - behind the
  // ranked ones, and stably among its peers - instead of jumping to the front on a 0.
  const UNRANKED = 100;
  const rankOf = (id: string) => rankById.get(id) ?? UNRANKED;
  const matchedChannels = (id: string, deliveryChannel: string) =>
    channelsFor(channelsById.get(id) ?? [], deliveryChannel);

  /*
    Henry's four slots, applied here rather than upstream.

    "look for 4 makes. If even 1 make no need for any near misses. If zero makes then post
    4 near misses. Never post the No's." - 2026-09-01, REVERSING the backfill rule this
    comment used to quote, under which two makes rendered alongside two near-misses. See
    slots.ts for why the two lists are now exclusive rather than complementary: a
    near-miss is homework, and homework is only worth printing when nobody will fund the
    file as it stands. Also one slot per BANK, not per program - "3 JLD programs should
    not take up 3 slots."

    The cap still belongs in the view, and that has not changed with the rule. The answer
    object is also what the eval suite scores, what the email digest is written from and
    what chat reasons over, and all three need the full sweep - a zero result is only a
    proof if nothing was trimmed before it was counted.

    Both inputs are decorated here because neither carries what the rule sorts on:

    - `stackRank` comes off the roster above. Migration 051 ranked all seventeen lenders
      (CapitalEdge Funding is 1) and rank is no longer a tiebreak - with a hard slice at
      four it decides WHICH lenders exist on the page, so a finding that arrives without
      it is a lender silently pushed to the back.
    - `maxLtv` decides which of one lender's programs gets that lender's single slot. It
      is NOT on the finding - it lives on the grid row - so it is joined back through the
      channel sentence; see channelsFor for how loose that join is. The maximum is taken
      over every row the sentence matched, because "the best a broker could size into on
      this channel" is the question the tiebreak is asking, and an unmatched sentence
      yields null rather than a zero, which slots.ts would read as a real ceiling of 0%.
      A finding whose channel cannot be recognised therefore loses the tiebreak to one
      that can, and ties fall back to synthesis's own order - which is the same behaviour
      as a lender whose grid rows genuinely print no ceiling.

    Near-misses get rank and NOTHING else: their schema is `{ lenderId, missBy,
    whatWouldFixIt, citationIds }`, with no channel to join on at all. So one lender with
    two near-misses collapses on first-wins, which keeps synthesis's own ordering. That
    is a real gap and not an oversight - the fix is a channel on the near-miss schema.
  */
  /*
    The decoration that used to live here moved into `slotAnswer` alongside the rule it
    feeds. It was duplicated in the email digest, the two copies drifted on exactly this
    input - stack rank, which a finding does not carry - and Henry got a mail naming four
    different lenders than the page he was looking at. One function now owns both halves,
    so there is nowhere left for them to disagree.
  */
  const slotted = answer
    ? slotAnswer(answer.findings, answer.nearMisses, allChannels)
    : { findings: [], nearMisses: [], hiddenMakes: 0 };

  const [open, setOpen] = useState<Reference | null>(null);

  // A chip whose reference has not arrived - or never will, because the server hit its
  // documented degraded path and sent an empty reference set - used to do NOTHING when
  // clicked. For a product whose whole claim is "with the citation", a dead chip is the
  // worst available failure: it looks like the answer has a source and the source will
  // not open, with nothing on screen saying why.
  const [citeMiss, setCiteMiss] = useState<number | null>(null);
  // And the far commoner case, which the miss notice was reporting as a failure: the
  // references event lands one frame after the answer. Between them the whole answer is
  // on screen with live chips and an empty map behind them, which is exactly the moment
  // somebody demonstrating this clicks the first chip. Remember which one they wanted
  // and open it when it arrives, rather than telling them it is broken and being wrong.
  const [citePending, setCitePending] = useState<number | null>(null);

  const onCite = (id: number): void => {
    const ref = references.get(id);
    if (ref) { setOpen(ref); setCiteMiss(null); setCitePending(null); return; }
    if (!referencesReady) { setCitePending(id); setCiteMiss(null); return; }
    setCiteMiss(id);
  };

  useEffect(() => {
    if (citePending === null) return;
    const ref = references.get(citePending);
    if (ref) { setOpen(ref); setCitePending(null); }
    // Only once nothing more is coming does a wait become a failure.
    else if (referencesReady) { setCiteMiss(citePending); setCitePending(null); }
  }, [citePending, references, referencesReady]);

  useEffect(() => {
    if (openCitationId == null) return;
    const ref = references.get(openCitationId);
    // A chip clicked in the chat that resolves to nothing used to be swallowed here -
    // onOpenHandled cleared the request and no panel opened, so the chip was dead in
    // exactly the way the miss notice below exists to prevent. Same handling now.
    if (ref) setOpen(ref);
    else if (referencesReady) setCiteMiss(openCitationId);
    else setCitePending(openCitationId);
    onOpenHandled?.();
  }, [openCitationId, references, referencesReady, onOpenHandled]);

  // The results start below the fold of a laptop screen - the intake form is eleven
  // fields tall - so clicking Search visibly did nothing at all. On a demo machine that
  // is the second-worst moment in the flow after the ninety-second wait itself.
  const top = useRef<HTMLDivElement | null>(null);
  useEffect(() => { bringIntoView(top.current); }, [startedAt]);

  // The miss notice renders at the top of a page that can be two thousand pixels tall,
  // and the chip that triggered it is usually near the bottom. Announcing a failure
  // somewhere the reader cannot see is the same as not announcing it.
  const notice = useRef<HTMLDivElement | null>(null);
  useEffect(() => { if (citeMiss !== null) bringIntoView(notice.current); }, [citeMiss]);

  // Two different failures wore one sentence, and "reload to try again" was the wrong
  // half of it for the one a client actually hit.
  //
  // A saved search re-opened days later carries the citation ids the answer was written
  // with, and until migration 066 every corpus rebuild renumbered them - 205 of the 242
  // citations referenced by searches run on 30 Aug pointed at nothing afterwards. Those
  // chips are not going to come back on a reload; the row they name is gone for good, and
  // telling somebody to reload sends them round a loop that cannot terminate.
  //
  // The two are distinguishable from here without asking the server anything. If the
  // reference set arrived with entries in it and this id is simply not among them, the
  // lookup worked and the citation is the thing that no longer exists. An EMPTY set is
  // the server's documented degraded path, which a reload genuinely can fix.
  const staleCorpus = references.size > 0;

  return (
    <div ref={top} style={{ maxWidth: 940, margin: '24px auto 60px', scrollMarginTop: 16 }}>
      {citeMiss !== null ? (
        <div ref={notice} className="card"
             style={{ marginBottom: 18, borderColor: 'var(--amber)' }}>
          <span style={{ fontSize: 14 }}>
            {/* A broker was never being shown a page, so telling them a page failed to
                load describes a feature they do not have. What actually failed is the
                reference lookup, and what they lose is the attribution. */}
            {sourceViewable
              ? `The source page for citation ${citeMiss} could not be loaded.`
              : `Citation ${citeMiss} could not be opened.`}
            {' '}The finding still stands — {staleCorpus
              ? 'this citation is from an earlier version of the corpus, and the ' +
                'document it quoted has been re-read since. Re-run the search and the ' +
                'answer will cite the current documents.'
              : 'reload to try again, or ask in chat and the answer will re-cite it.'}
          </span>
          <button type="button" className="chip" style={{ marginLeft: 10 }}
                  onClick={() => setCiteMiss(null)}>dismiss</button>
        </div>
      ) : null}

      {citePending !== null ? (
        <div className="card" style={{ marginBottom: 18, borderColor: 'var(--blue-line)' }}>
          <span className="spin" />
          <span style={{ fontSize: 14, marginLeft: 10 }}>
            {sourceViewable
              ? `Fetching the source page for citation ${citePending}`
              : `Fetching citation ${citePending}`} — it opens on its own.
          </span>
        </div>
      ) : null}

      <SearchProgress stages={stages} startedAt={startedAt} sections={sections}
                      failed={failed} detailed={sweep} />

      {/*
        The two things about a search that change what its numbers MEAN, said before the
        numbers.

        Both are cheap to render and expensive to omit. A widened sweep quotes each lender
        off the most permissive row that still fits, so an 80% on this page is a ceiling
        and not a quote - and the verdicts frame lands about eighty seconds before the
        prose that would explain that. A matrix-only answer read no guideline at all,
        which is a narrower claim than the same page made yesterday with the box filled
        in, and nothing else on screen distinguishes them.
      */}
      {(coverage?.widenedOn?.length ?? 0) > 0 ? (
        <div className="card" style={{ marginBottom: 18, borderColor: 'var(--amber)' }}>
          <span style={{ fontSize: 14 }}>
            <b>Widened search.</b> You left{' '}
            {coverage!.widenedOn!.map((f) => WIDENED_LABELS[f] ?? f).join(', ')} blank, so
            every value was allowed to match and each lender is shown at the most
            permissive row that still fits. These numbers can only fall once those fields
            are filled in - treat them as ceilings, not as quotes.
          </span>
        </div>
      ) : null}

      {basis === 'matrix_only' ? (
        <div className="card" style={{ marginBottom: 18, borderColor: 'var(--blue-line)' }}>
          <span style={{ fontSize: 14 }}>
            <b>Rate matrices only.</b> Nothing was noted as unusual about this file, so
            the search read the grids and the fine print printed beside them - no
            underwriting guideline. If there is something funky about it, describe it in
            the box and the guidelines get read too.
          </span>
        </div>
      ) : null}

      {/*
        The sweep, in full, for the account that has to debug it.

        "Swept 17 lenders across 40 channels" is our accounting. It answers a question a
        broker did not ask - they want to know who can do this file, not how many doors we
        knocked on - and the same line is the first thing we look at when an answer is
        wrong, because it separates "no lender does this" from "the panel was empty".
        Everything in this card except the empty-panel warning is in that category, and
        the warning is re-stated below in words a broker can use.
      */}
      {sweep && coverage ? (
        <div className="card" style={{ marginBottom: 18 }}>
          <div style={{ fontSize: 14, color: 'var(--ink-2)' }}>
            Swept <b>{coverage.lendersEvaluated}</b> lenders across{' '}
            <b>{coverage.channelsEvaluated}</b> delivery channels —{' '}
            <b>{coverage.eligible}</b> eligible, <b>{coverage.nearMisses}</b> near-miss.
          </div>
          {/*
            Zero lenders swept is not zero lenders willing. It means the panel this
            search ran against was empty - every lender inactive, or none seeded at all -
            and the line above renders that as "Swept 0 lenders ... 0 eligible", which a
            broker reads as a unanimous decline. It is the single worst misreading this
            screen can produce, and it is a corpus state somebody has to go and fix.
          */}
          {coverage.lendersEvaluated === 0 ? (
            <div style={{ marginTop: 10, fontSize: 13, color: 'var(--amber)' }}>
              No lender was evaluated at all — there is nothing active in the panel to
              check this file against. That is a gap on our side, not an answer about
              this loan.
            </div>
          ) : null}

          {/*
            Nothing clearing the grid is a normal mid-search state, not a result. The
            matrix is a coarse filter; a carve-out read out of the guidelines can put a
            file back that the grid declined, and that reading has not happened yet.
            Without this line the broker spends the whole synthesis wait looking at
            "0 eligible" and concluding the search is over.
          */}
          {coverage.lendersEvaluated > 0 && coverage.eligible === 0 && !answer ? (
            <div style={{ marginTop: 10, fontSize: 13, color: 'var(--muted)' }}>
              Nothing clears on the grid alone. The guidelines are still being read — an
              exception written into the prose can put a file back that the matrix
              declined, so this is not the answer yet.
            </div>
          ) : null}

          {/*
            Two different sentences, because they are two different facts and saying the
            wrong one is what prompted this: "I provided like 8 Arc matrices... why is it
            saying we hold no matrix?" We held twelve. His $2,850,000 loan was above every
            band Arc prints, which is a fact about the loan, not about our files.

            `noDataFor` is every channel that found no cell. `noMatrixFor` is the strict
            subset where we hold nothing at all - so only the second may be called a gap
            in our files. The difference between them is a lender whose grid simply does
            not reach these coordinates, and telling a broker we have no documents from a
            lender we have twelve documents from is the version that loses their trust.

            When the server does not send noMatrixFor we say NEITHER. An older server
            cannot distinguish the two, and guessing would reintroduce the bug it fixed.
          */}
          {(coverage.noMatrixFor?.length ?? 0) > 0 ? (
            <div style={{ marginTop: 10, fontSize: 13, color: 'var(--muted)' }}>
              <b>No current document</b> for {coverage.noMatrixFor!.join(', ')}. That is a
              gap in our files, not a decline by those lenders.
            </div>
          ) : null}
          {coverage.noMatrixFor
            && coverage.noDataFor.length > coverage.noMatrixFor.length ? (
            <div style={{ marginTop: 10, fontSize: 13, color: 'var(--muted)' }}>
              <b>Off the grid we hold</b> for{' '}
              {coverage.noDataFor.filter((s) => !coverage.noMatrixFor!.includes(s)).join(', ')}.
              We hold their matrices; this file falls outside the bands they publish.
            </div>
          ) : null}
          {coverage.staleDocuments.map((s) => (
            <div key={`${s.lenderSlug}:${s.documentTitle}`} style={{ marginTop: 8 }}>
              <span className="pill pill-stale">stale</span>{' '}
              <span style={{ fontSize: 13, color: 'var(--muted)' }}>
                {s.lenderSlug} · “{s.documentTitle}” is {s.ageDays} days old — confirm
                against a current issue.
              </span>
            </div>
          ))}
        </div>
      ) : null}

      {/*
        The one line out of that card a broker must still get.
        Without the coverage card there is nothing on a broker's screen between "Done" and
        a page with no lender on it, and an empty result reads as a unanimous decline -
        the single worst misreading this screen can produce. It is a corpus state on our
        side, so it is said as one, without the count that would only invite the question
        of what the number should have been.
      */}
      {/*
        A SYSTEM FAILURE, and it is drawn to be told apart from everything under it.

        Red, not amber, and first. Every other notice on this page is amber and every one
        of them is about the loan - a widened axis, a stale document, a finding whose
        citation did not hold. This one is about us. The requirement is Henry's and it is
        about exactly this confusion: "any system-error like 400, after u retry
        sufficiently, and it fails - that should not get swallowed up quietly... otherwise
        we get endless confusion", and "we want clear delineation of system-failures vs.
        actual business logic failures/model failures."

        The confusion it prevents is specific and expensive. A per-lender call that dies
        of an infrastructure error leaves that lender out of the answer, and an absent
        lender on this page is read as a lender who cannot do the loan - so a broker stops
        calling a bank that would have funded the deal, and we never hear about it. It
        also mimics, exactly, a real deletion defect, which means an engineer reading a
        screenshot cannot tell the two apart either.

        The re-run line and the names sit in ONE card on purpose. Two notices - "something
        failed" here and "you may want to re-run" somewhere else - is a reader's job we
        can do for them, and the names are what makes the advice specific enough to act
        on.
      */}
      {systemFailure ? (
        <div className="card" style={{ marginBottom: 18, borderColor: 'var(--red)' }}>
          <h3 style={{ margin: '0 0 8px', fontSize: 17, color: 'var(--red)' }}>
            {systemFailure.title}
          </h3>
          <p style={{ margin: 0, lineHeight: 1.6, fontSize: 14 }}>
            {systemFailure.detail}{' '}<b>{systemFailure.action}</b>
          </p>
        </div>
      ) : null}

      {!sweep && coverage && coverage.lendersEvaluated === 0 ? (
        <div className="card" style={{ marginBottom: 18, borderColor: 'var(--amber)' }}>
          <span style={{ fontSize: 14 }}>
            This file was not checked against anything — our lender panel came back empty,
            which is a gap on our side and not an answer about this loan. Nothing here
            means no lender will do it.
          </span>
        </div>
      ) : null}

      {droppedFindings > 0 ? (
        <div className="card" style={{ marginBottom: 18, borderColor: 'var(--amber)' }}>
          <span style={{ fontSize: 14 }}>
            {droppedFindings === 1 ? 'One finding was' : `${droppedFindings} findings were`}
            {' '}removed because {droppedFindings === 1 ? 'its' : 'their'} citations could
            not be verified against the source documents. That lender may still be able to
            do this file — it is missing here rather than declined.
          </span>
        </div>
      ) : null}

      {expertReview ? (
        <div className="card" style={{ marginBottom: 18, borderColor: 'var(--blue)' }}>
          <h3 style={{ margin: '0 0 10px', fontSize: 20 }}>We'll take this one on</h3>
          <p style={{ margin: 0, lineHeight: 1.65, fontSize: 15 }}>{expertReview}</p>
        </div>
      ) : null}

      {/*
        The summary, and ONLY for the account that can already see the whole sweep.

        It is not being retired - synthesis still writes it, the answer object still
        carries it, and the email digest still leads with it, which is where the client
        asked for it to stay. It comes off the BROKER's page because of what it is: four
        sentences of prose that name every lender the search touched, including the
        declines and the ones we hold no document for. On one real file that was thirteen
        lenders in a paragraph.

        That paragraph undoes the entire selection rule sitting directly beneath it. The
        four slots exist so a broker reads four banks that can do the loan; a summary that
        opens "two of the seventeen can do this, and here is what the other fifteen said"
        puts the fifteen back, in prose, above the cards - and prose is harder to ignore
        than a list, not easier. "Never post the No's" is not a rule about cards.

        A superuser keeps it for the same reason they keep the coverage line: it is the
        fastest read on whether the answer is any good, and it is what the digest they are
        about to send will say.
      */}
      {sweep && answer ? (
        <div className="card" style={{ marginBottom: 18 }}>
          <h3 style={{ margin: '0 0 10px', fontSize: 20 }}>Summary</h3>
          <p style={{ margin: 0, lineHeight: 1.65, fontSize: 15 }}>
            <Prose text={answer.summary} onCite={onCite} />
          </p>
        </div>
      ) : null}

      {/*
        Why the coverage line says "3 near-miss" and no near-miss card follows.

        There are now TWO ways that happens and they mean opposite things, so the card
        says which. The old one: the grid counts a channel as a near-miss on LTV headroom
        alone, and the model drops it once the guidelines decline the file outright for an
        unrelated reason - the answer wrote none. The new one, since the slot rule
        reversed on 2026-09-01: the answer wrote several and the page is deliberately
        printing none of them, because one lender can do the file as it stands and a
        near-miss beside a make is homework nobody needs.

        Reconciling against `slotted` rather than `answer` is what makes the second case
        visible at all; against the raw answer the card would go quiet exactly when the
        discrepancy it exists to explain is at its largest.

        What is gone from the copy: "the reasons are in the lender cards below". Declines
        no longer render, so that sentence pointed at cards that do not exist. It was
        written when every decline rode along under the makes as evidence.
      */}
      {/* Shown only where the count it reconciles was shown. A broker who never read
          "3 near-miss" has no hole to explain, and explaining one anyway would introduce
          a grid number purely to walk it back. */}
      {sweep && answer && slotted.nearMisses.length === 0
        && (coverage?.nearMisses ?? 0) > 0 ? (
        <div className="card" style={{ marginBottom: 18 }}>
          <div style={{ fontSize: 14, color: 'var(--ink-2)', lineHeight: 1.6 }}>
            The grid flagged {coverage!.nearMisses} channel
            {coverage!.nearMisses === 1 ? '' : 's'} as close on the numbers.{' '}
            {answer.nearMisses.length > 0
              ? `The answer wrote up ${answer.nearMisses.length} of them and none is `
                + 'shown: a lender can do this file as it stands, which is what makes a '
                + 'near-miss moot. They are in the answer object either way.'
              : 'None of them survived a read of the guidelines as a fixable near-miss, '
                + 'so the answer wrote none.'}
          </div>
        </div>
      ) : null}

      {answer && slotted.nearMisses.length > 0 ? (
        <div className="card" style={{ marginBottom: 18, borderColor: 'var(--amber)' }}>
          <h3 style={{ margin: '0 0 10px', fontSize: 20 }}>Close — and fixable</h3>
          {slotted.nearMisses.map((n, i) => (
            <div key={i} style={{ marginBottom: 12 }}>
              <div className="serif" style={{ fontSize: 16, fontWeight: 600 }}>
                {nameOf(n.lenderId)}
              </div>
              <div style={{ fontSize: 14, color: 'var(--ink-2)' }}>
                <Prose text={n.missBy} onCite={onCite} />
              </div>
              <div style={{ fontSize: 14, marginTop: 4, fontWeight: 600 }}>
                → <Prose text={n.whatWouldFixIt} onCite={onCite} />
                {n.citationIds.map((id) => (
                  <button key={id} className="chip" type="button" onClick={() => onCite(id)}>▸ {id}</button>
                ))}
              </div>
              {/* The AE belongs on a near-miss more than anywhere else on the page. This
                  card only renders when nothing could do the file as it stands, so the
                  next move is a conversation about an exception - which is a person, not
                  a form. */}
              <div className="card-actions">
                <AeContactCard lenderId={n.lenderId} inquiryId={inquiryId}
                               contact={aeById.get(n.lenderId) ?? null} />
                {sourceViewable ? (
                  <ViewMatrixButton lenderName={nameOf(n.lenderId)}
                                    programs={nearMissPrograms(channelsById.get(n.lenderId) ?? [],
                                      `${n.missBy} ${n.whatWouldFixIt}`)} />
                ) : null}
              </div>
            </div>
          ))}
        </div>
      ) : null}

      {slotted.findings.map((f, i) => (
        <div className="card" key={i} style={{ marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            {verdictPill(f.verdict)}
            {/*
              The lender's name. It was missing entirely: findings carry lenderId, which
              is a UUID, and the only place a name appeared was the grid block below -
              which is hidden as soon as the answer arrives. So the names showed for the
              ninety seconds of synthesis and then vanished, leaving cards that read
              "Yes - conditions   base / retained" with no way to tell whose answer it
              was. Naming the lender is the product's entire job.
            */}
            <span className="serif" style={{ fontSize: 17, fontWeight: 600 }}>
              {nameOf(f.lenderId)}
            </span>
            {/*
              And the program, where naming it adds something. The lender's own name is
              printed immediately to the left, so this says "TransAm program" rather than
              "JLD TransAm program" - the pair reads as one label and printing the lender
              twice on one line reads as a bug. A retained channel yields null and the
              name stands alone, which is the right label for it: there is no second name
              to give, and "base / retained" was our word for that, not the lender's.

              One card per lender now (slots.ts), so this is never a way to tell two of a
              lender's programs apart on screen - it is how the surviving one is named.
            */}
            {(() => {
              const label = programLabel(f.deliveryChannel,
                                         matchedChannels(f.lenderId, f.deliveryChannel));
              return label
                ? <span style={{ fontSize: 13, color: 'var(--muted)' }}>{label}</span>
                : null;
            })()}
          </div>
          <div className="serif" style={{ fontSize: 18, margin: '10px 0 6px' }}>
            <Prose text={f.headline} onCite={onCite} />
          </div>
          {f.blockers.length > 0 ? (
            <>
              <div className="lbl" style={{ marginTop: 12 }}>Blockers</div>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {f.blockers.map((b, j) => (
                  <CitedLine key={j} item={b} onCite={onCite} />
                ))}
              </ul>
            </>
          ) : null}
          {f.carveOutsApplied.length > 0 ? (
            <>
              <div className="lbl" style={{ marginTop: 12 }}>Exceptions that apply to you</div>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {f.carveOutsApplied.map((c, j) => (
                  <li key={j} style={{ marginBottom: 8, lineHeight: 1.55 }}>
                    <Prose text={c.text} onCite={onCite} />
                    <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 2 }}>
                      Because: {c.satisfiedBy}
                    </div>
                  </li>
                ))}
              </ul>
            </>
          ) : null}
          {f.conditions.length > 0 ? (
            <>
              <div className="lbl" style={{ marginTop: 12 }}>Conditions</div>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {f.conditions.map((c, j) => <CitedLine key={j} item={c} onCite={onCite} />)}
              </ul>
            </>
          ) : null}
          {/* Last on the card, under the conditions, because it is the next action and
              the conditions are what the broker will be discussing when they call. */}
          {/* The matrix beside it, gated on the same predicate the page routes ask - so a
              level that is ever narrowed again loses the button rather than gaining a
              viewer full of refusals. */}
          <div className="card-actions">
            <AeContactCard lenderId={f.lenderId} inquiryId={inquiryId}
                           contact={aeById.get(f.lenderId) ?? null} />
            {sourceViewable ? (
              <ViewMatrixButton lenderName={nameOf(f.lenderId)}
                                programs={quotedPrograms(channelsById.get(f.lenderId) ?? [],
                                  matchedChannels(f.lenderId, f.deliveryChannel),
                                  `${f.deliveryChannel} ${f.headline}`)} />
            ) : null}
          </div>
        </div>
      ))}

      {/*
        An answer with no findings at all. Reachable two ways, and they need opposite
        things said about them.
        Nobody could do the file: the server has already computed that and sent the
        expert-review offer, which renders above, so this stays quiet rather than
        repeating it in worse words.
        Every finding was dropped by the citation validator: needsExpertReview
        deliberately withholds the offer there, because "nobody can do this" is a claim
        nothing checked. The amber notice above says findings were removed, but nothing
        said the lender cards are missing BECAUSE of it - so the page just ended after
        the summary, and an answer that stops without naming a single lender reads as
        broken rather than as incomplete.
      */}
      {answer && answer.findings.length === 0 && !expertReview ? (
        <div className="card" style={{ marginBottom: 18 }}>
          <h3 style={{ margin: '0 0 10px', fontSize: 20 }}>No lender-by-lender findings</h3>
          <p style={{ margin: 0, lineHeight: 1.65, fontSize: 15, color: 'var(--ink-2)' }}>
            {droppedFindings > 0
              ? 'Every finding this run produced was removed by the citation check, so '
                + 'there is nothing here we can stand behind with a source. Run the '
                + 'search again, or ask a follow-up below — a second pass usually cites '
                + 'cleanly.'
              : 'The answer came back without any per-lender findings. The summary above '
                + 'is what it produced. Ask a follow-up below and it will re-check the '
                + 'documents.'}
          </p>
        </div>
      ) : null}

      {/*
        The roster: every lender, every delivery channel, every no-data gap. It exists to
        put something true on screen at two seconds while synthesis runs for another
        eighty-eight, and to let us see what the matrix said before the model touched it.
        A broker does not need to watch seventeen banks be evaluated, and reading a
        pre-answer grid that the cited answer then supersedes is how they end up quoting a
        number no finding stands behind.
      */}
      {sweep && lenders && lenders.length > 0 && !answer ? (
        <div className="card">
          <h3 style={{ margin: '0 0 12px', fontSize: 20 }}>Grid results</h3>
          {lenders.map((l) => (
            <div key={l.lenderSlug} style={{ marginBottom: 16 }}>
              <div style={{ fontWeight: 600 }}>
                {l.lenderName}
                {l.channelsDisagree ? (
                  <span style={{ marginLeft: 8, fontSize: 12, color: 'var(--amber)' }}>
                    ⚠ programs disagree — ask your AE which program fits this file
                  </span>
                ) : null}
              </div>
              {/*
                no_data channels are collapsed to one line. They are a real answer -
                "we hold no current matrix row at these coordinates" is a gap on our side
                rather than a decline, and hiding it would overstate coverage - but a
                lender that prices the file on one channel and holds no row on eight
                others listed nine lines, of which eight said "No document". The one
                that matters was last and read as an afterthought.
              */}
              {/*
                Channels named as the lender's programs here too, for one reason only:
                this grid is what we read while an answer is being debugged, and a label
                that does not match the card the broker ends up looking at makes the two
                views harder to hold together. The information is not lost - the program
                name still leads, and a delivery channel is still one line per channel.
              */}
              {l.channels.filter((c) => c.verdict !== 'no_data').map((c, i) => (
                <div key={i} style={{ fontSize: 13, color: 'var(--muted)', marginTop: 4 }}>
                  {verdictPill(c.verdict)}{' '}
                  {c.programName}{c.isBaseChannel ? '' : ` · ${c.investorName} program`}
                  {c.maxLtv !== null
                    ? ` · max ${c.maxLtv}% vs ${c.requestedLtv.toFixed(2)}%`
                    : ''}
                  {c.maxLoanAtMaxLtv !== null && (c.ltvHeadroom ?? 0) < 0
                    ? ` · fits at ${money(c.maxLoanAtMaxLtv)}`
                    : ''}
                </div>
              ))}
              {(() => {
                const gaps = l.channels.filter((c) => c.verdict === 'no_data');
                if (gaps.length === 0) return null;
                const priced = l.channels.length - gaps.length;
                return (
                  <details style={{ fontSize: 13, color: 'var(--muted)', marginTop: 4 }}>
                    <summary style={{ cursor: 'pointer' }}>
                      {gaps.length} other {gaps.length === 1 ? 'channel' : 'channels'} with
                      {' '}no current matrix row for this file
                      {priced === 0 ? ' — nothing priced here' : ''}
                    </summary>
                    {gaps.map((c, i) => (
                      <div key={i} style={{ marginTop: 4, paddingLeft: 12 }}>
                        {c.programName}{c.isBaseChannel ? '' : ` · ${c.investorName} program`}
                      </div>
                    ))}
                  </details>
                );
              })()}
            </div>
          ))}
        </div>
      ) : null}

      {open ? (
        // Two flags, not one, since 2026-09-08. `sourceViewable` is now true for every
        // account and governs the lender's own page and paragraphs; `sweep` still governs
        // OUR bookkeeping inside the same panel - how old our copy of the document is,
        // and whether the grid route answered. They were the same boolean while they
        // happened to agree.
        <CitationViewer reference={open} sourceViewable={sourceViewable} staffView={sweep}
                        onClose={() => setOpen(null)} />
      ) : null}
    </div>
  );
}
